import { useAtom } from 'jotai';
import { useState, useCallback } from 'react';
import { useRouter } from '@/hooks';
import { CircularProgress, Stack } from '@mui/material';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { ReportComments } from '../../shared/ReportComments';
import { Snackbar } from '@/components/shared/Snackbar';
import { Alert } from '@/components/shared/Alert';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import * as state from '@/components/Analysis/state';
import { NotificationAction, NotificationText, notificationAlertStyles } from '../styles';
import {
  PointOfInterestsFragment,
  ReportInternalType,
  useReportPointOfInterestVerifiedSubscription,
} from '@/__generated__/graphql';
import { ReportNavigator } from './ReportNavigator';
import { EquipmentsList } from './EquipmentsList';
import { ReportOrder } from './types';
import { abyssColors } from '@/theme/colors';

export const PoiReportContainer = () => {
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [showVerifiedAlert, setShowVerifiedAlert] = useState<boolean>(false);
  const [suppressAlerts, setAlertSuppression] = useState<boolean>(false);
  const [verifiedPOIs, setVerifiedPoints] = useState<PointOfInterestsFragment[]>([]);
  const [orderUpdateLoader, setOrderUpdateLoader] = useState<boolean>(false);
  const [orderOfReport, setOrderOfReport] = useState<ReportOrder>(() => {
    const reportOrder = selectedReport?.reportOrder;
    return {
      assemblyOrder: reportOrder?.assemblyOrder?.filter((s): s is string => !!s) || [],
      poiOrder: (reportOrder?.poiOrder as { [key: string]: string[] }) || {},
    };
  });

  const router = useRouter();

  const selectedReportId = router?.query?.reportid;

  const { shouldShow, content, persistent, duration } = snackbarMessage;
  const navbarHeight = useNavbarHeight();

  const updateVerifiedPOI = useCallback(
    (verifiedPointOfInterest: Partial<PointOfInterestsFragment>) => {
      if (verifiedPOIs.some((poi) => poi.id === verifiedPointOfInterest.id)) {
        setVerifiedPoints((previous) => {
          return previous.map((poi) => {
            if (poi.id === verifiedPointOfInterest.id) {
              return {
                ...poi,
                ...verifiedPointOfInterest,
              };
            }
            return poi;
          });
        });
      } else {
        if (
          selectedReport &&
          selectedReport.pointOfInterests?.some((poi) => poi.id === verifiedPointOfInterest.id)
        ) {
          const updatedPOI = selectedReport?.pointOfInterests
            .filter((poi) => poi.id === verifiedPointOfInterest.id)
            .map((poi) => ({
              ...poi,
              ...verifiedPointOfInterest,
            }));
          setVerifiedPoints((previous) => {
            return [...previous, ...updatedPOI];
          });
        }
      }

      if (!suppressAlerts) {
        setShowVerifiedAlert(true);
      }
    },
    [selectedReport, suppressAlerts, verifiedPOIs]
  );

  useReportPointOfInterestVerifiedSubscription({
    variables: {
      reportId: typeof selectedReportId === 'string' ? selectedReportId : '',
    },
    onData: ({ data: verifiedNotification }) => {
      if (verifiedNotification?.data?.reportPointOfInterestVerified) {
        const verifiedPointOfInterest =
          verifiedNotification?.data?.reportPointOfInterestVerified.pointOfInterest;
        if (verifiedPointOfInterest) {
          updateVerifiedPOI(verifiedPointOfInterest);
        }
      }
    },
  });

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const closeNotification = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowVerifiedAlert(false);
  };

  const handleDismissAlert = () => {
    setShowVerifiedAlert(false);
    setAlertSuppression(true);
  };

  const handleRefresh = () => {
    setSelectedReport((previous) => {
      if (!previous) return undefined;
      if (verifiedPOIs.length > 0 && previous.pointOfInterests) {
        const updatedPOIs = previous.pointOfInterests.map((poi) => {
          const verifiedPOI = verifiedPOIs.find((verified) => verified.id === poi.id);
          return verifiedPOI ?? poi;
        });
        return {
          ...previous,
          pointOfInterests: updatedPOIs,
        };
      }
      return previous;
    });
    setShowVerifiedAlert(false);
  };

  return (
    <>
      <Stack direction="row">
        <ReportNavigator
          setOrderUpdateLoader={setOrderUpdateLoader}
          orderOfReport={orderOfReport}
          setOrderOfReport={setOrderOfReport}
        />
        {orderUpdateLoader ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ width: '100%', minHeight: '100%' }}
          >
            <CircularProgress sx={{ color: abyssColors.primary[300] }} size="10rem" />
          </Stack>
        ) : (
          <EquipmentsList reportType={ReportInternalType.PointOfInterest} />
        )}
      </Stack>

      <ReportComments />
      <Snackbar 
        open={shouldShow} 
        onClose={closeSnack}
        persistent={persistent}
        autoHideDuration={duration}
      >
        {content}
      </Snackbar>
      <Snackbar
        open={showVerifiedAlert}
        onClose={closeNotification}
        sxProps={{ top: `${navbarHeight + 60}px !important` }}
        persistent
      >
        <Alert
          severity="info"
          action={<AbyssIcon iconName="close" pointer onClick={handleDismissAlert} />}
          sxProps={notificationAlertStyles}
        >
          <Stack gap="16px" direction="row" alignItems="center" justifyContent="space-between">
            <NotificationText>
              POI’s are now verified, Please refresh to add post inspection data to this report
            </NotificationText>
            <NotificationAction onClick={handleRefresh}>Refresh</NotificationAction>
          </Stack>
        </Alert>
      </Snackbar>
    </>
  );
};
